/**
 * Pure i18n resolution — no React, no DOM. Tested by scripts/i18n.test.ts
 * under node --test, and reused by the provider and the prerender build.
 */

export const LOCALES = ["en", "ru", "uz"] as const
export type Locale = (typeof LOCALES)[number]

export function isLocale(v: unknown): v is Locale {
  return typeof v === "string" && (LOCALES as readonly string[]).includes(v)
}

/** Locale priority: URL prefix (/ru, /uz) > stored choice > browser languages > en. */
export function detectLocale(
  pathname: string,
  stored: string | null,
  languages: readonly string[],
): Locale {
  for (const locale of ["ru", "uz"] as const) {
    if (pathname === `/${locale}` || pathname.startsWith(`/${locale}/`)) return locale
  }
  if (isLocale(stored)) return stored
  for (const lang of languages) {
    const base = lang.toLowerCase().split("-")[0]
    if (isLocale(base)) return base
  }
  return "en"
}

function lookup(catalog: object, key: string): unknown {
  let node: unknown = catalog
  for (const part of key.split(".")) {
    if (typeof node !== "object" || node === null) return undefined
    node = (node as Record<string, unknown>)[part]
  }
  return node
}

function pluralKey(locale: Locale, key: string, count: number): string {
  const form = new Intl.PluralRules(locale).select(count)
  return `${key}_${form}`
}

function fromCatalog(
  catalog: object,
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string | undefined {
  if (typeof vars?.count === "number") {
    const plural = lookup(catalog, pluralKey(locale, key, vars.count))
    if (typeof plural === "string") return plural
  }
  const value = lookup(catalog, key)
  if (typeof value === "string") return value
  // Plural-only key (no bare form): fall back to _other even without a numeric count.
  const other = lookup(catalog, `${key}_other`)
  return typeof other === "string" ? other : undefined
}

/** Resolve a message: active catalog -> en catalog -> the key itself. Never throws. */
export function resolveMessage(
  catalog: object,
  fallback: object,
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const raw = fromCatalog(catalog, locale, key, vars) ?? fromCatalog(fallback, "en", key, vars) ?? key
  if (!vars) return raw
  return raw.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  )
}
